import * as THREE from "three";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";

export type BossModelState = "hidden" | "loading" | "ready" | "error";
export const WORLD_BOSS_ASSET = "zmb.boss_titan_necrolith";
export const WORLD_BOSS_SCALE = 0.33;

/** One actual animated Titan. Cached model geometry/materials remain owned by the loader. */
export class WorldBoss {
  readonly group = new THREE.Group();
  private readonly ring: THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>;
  private instance: ModelInstance | null = null;
  private state: BossModelState = "hidden";
  private target: { x: number; y: number; until: number } | null = null;
  private pending = false;
  private failed = false;
  private disposed = false;

  constructor(private readonly onState: (state: BossModelState) => void = () => {}) {
    this.group.name = "world-boss-titan";
    this.group.visible = false;
    this.group.userData["assetId"] = WORLD_BOSS_ASSET;
    this.ring = new THREE.Mesh(new THREE.TorusGeometry(1.43, 0.024, 5, 56), new THREE.MeshBasicMaterial({ color: 0xcfae6d, transparent: true, opacity: 0.7, depthWrite: false }));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.16;
    this.group.add(this.ring);
    this.setState("hidden");
  }

  private setState(state: BossModelState): void {
    this.group.userData["state"] = state;
    if (this.state === state) return;
    this.state = state; this.onState(state);
  }

  setTarget(target: { x: number; y: number; until: number } | null): void {
    if (this.disposed) return;
    this.target = target ? { ...target } : null;
    this.group.visible = !!target;
    if (!target) { this.setState("hidden"); return; }
    this.group.position.set(target.x, 0.04, target.y);
    if (this.instance) this.setState("ready");
    else if (this.failed) this.setState("error");
    else if (this.pending) this.setState("loading");
    else this.load();
  }

  private load(): void {
    if (this.pending || this.disposed) return;
    this.pending = true; this.setState("loading");
    void loadModel(WORLD_BOSS_ASSET, "fresh").then((model) => {
      if (this.disposed) return;
      const actor = instantiate(model);
      actor.object.name = "world-boss-model";
      actor.object.scale.setScalar(WORLD_BOSS_SCALE);
      actor.object.position.y = 0.02;
      actor.play("idle");
      this.instance = actor; this.group.add(actor.object);
      this.group.userData["modelUrl"] = model.url;
      this.group.userData["clip"] = actor.actions["idle"] ? "idle" : null;
      this.failed = false;
      this.setState(this.target ? "ready" : "hidden");
    }).catch(() => {
      if (this.disposed) return;
      this.failed = true; this.setState(this.target ? "error" : "hidden");
    }).finally(() => { this.pending = false; });
  }

  retry(): void {
    if (!this.target || !this.failed || this.disposed) return;
    this.failed = false; this.load();
  }

  update(dt: number, now: number, reducedMotion: boolean): void {
    if (this.disposed) return;
    this.group.visible = !!this.target && this.target.until > now;
    if (!this.group.visible) return;
    if (this.instance) {
      if (reducedMotion) this.instance.mixer.setTime(0);
      else this.instance.mixer.update(Math.min(Math.max(dt, 0), 0.1));
      this.group.userData["animationTime"] = this.instance.mixer.time;
    }
    this.group.userData["reducedMotion"] = reducedMotion;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.instance) {
      this.instance.mixer.stopAllAction(); this.instance.mixer.uncacheRoot(this.instance.object);
      this.instance.object.removeFromParent(); this.instance = null;
    }
    this.ring.geometry.dispose(); this.ring.material.dispose();
    this.group.removeFromParent(); this.group.clear(); this.target = null;
  }
}
